"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useRouter, usePathname } from "next/navigation";

interface UserInfo {
  first_name: string;
  last_name: string;
  email?: string;
  oidc?: string;
  expiry_date?: string;
  [key: string]: unknown;
}

interface AppConfig {
  userInfo: UserInfo;
  featureFlags?: Record<string, boolean>;
  authMode?: string; // "oidc" or "atomconfig"
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  userInfo: UserInfo | null;
  isTokenExpired: boolean;
  authMode: string; // "oidc" or "atomconfig"
  login: (redirectPath: string, idp: string, oidcPath?: string) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Use empty string to make requests relative to current origin (will use Next.js proxy)
const API_BASE_URL = process.env.NEXT_PUBLIC_URM_API_URL || "";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [authMode, setAuthMode] = useState<string>("oidc");
  const router = useRouter();
  const pathname = usePathname();

  const checkAuth = async () => {
    // Skip auth check on login page
    if (pathname === "/login") {
      setIsLoading(false);
      setIsTokenExpired(false); // Reset token expired state on login page
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/appconfig`, {
        credentials: "include", // Important: include cookies for session
      });

      if (response.ok) {
        const data: AppConfig = await response.json();
        setUserInfo(data.userInfo);
        setAuthMode(data.authMode || "oidc");
        setIsAuthenticated(true);
      } else if (response.status === 401) {
        // Not authenticated
        setIsAuthenticated(false);
        setUserInfo(null);
        // Redirect to login with current path as redirectUrl
        router.push(`/login?redirectUrl=${encodeURIComponent(pathname)}`);
      } else {
        throw new Error("Failed to fetch app config");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setIsAuthenticated(false);
      setUserInfo(null);
      // Redirect to login on error as well (e.g., network issues)
      router.push(`/login?redirectUrl=${encodeURIComponent(pathname)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (redirectPath: string, idp: string, oidcPath?: string) => {
    // Build login URL - if API_BASE_URL is empty, use relative path
    const baseUrl = API_BASE_URL || window.location.origin;
    const url = new URL(`${baseUrl}/auth/login`);
    // Default to /resources if redirectPath is root
    const finalRedirectPath = redirectPath === "/" ? "/resources" : redirectPath;
    url.searchParams.set("redirecturl", finalRedirectPath);
    url.searchParams.set("idp", idp);
    if (idp === "custom.idp" && oidcPath) {
      url.searchParams.set("oidcPath", oidcPath);
    }
    // Full page redirect to backend login endpoint
    window.location.href = url.toString();
  };

  const logout = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/logout`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const idpLogoutUrl = await response.text();
        setUserInfo(null);
        setIsAuthenticated(false);

        // Check if it's a valid URL
        try {
          new URL(idpLogoutUrl);
          window.location.href = idpLogoutUrl;
        } catch {
          // If not a valid URL, just redirect to login page
          router.push("/login");
        }
      } else {
        // Fallback: clear state and redirect to login
        setUserInfo(null);
        setIsAuthenticated(false);
        router.push("/login");
      }
    } catch (error) {
      console.error("Logout failed:", error);
      // Fallback: clear state and redirect to login
      setUserInfo(null);
      setIsAuthenticated(false);
      router.push("/login");
    }
  };

  useEffect(() => {
    // Add a small delay to allow cookies to be processed after OAuth redirect
    // This prevents the race condition where API calls fire before the browser
    // has fully processed the Set-Cookie header from the callback
    const timer = setTimeout(() => {
      checkAuth();
    }, 100); // 100ms delay

    return () => clearTimeout(timer);
  }, [pathname]);

  // Token expiry checker - runs every minute
  // Skip token expiry checks in ATOMCONFIG mode
  useEffect(() => {
    // Skip token expiry checks in ATOMCONFIG mode (uses file-based config, no token expiry)
    if (authMode === "atomconfig") {
      return;
    }

    if (!isAuthenticated || !userInfo?.expiry_date) {
      return;
    }

    const checkTokenExpiry = () => {
      try {
        const expiryDate = new Date(userInfo.expiry_date!);
        const now = new Date();

        // Check if token has expired
        if (now >= expiryDate) {
          console.log("OIDC token has expired");
          setIsTokenExpired(true);
        }
      } catch (error) {
        console.error("Error checking token expiry:", error);
      }
    };

    // Check immediately on mount
    checkTokenExpiry();

    // Then check every minute
    const interval = setInterval(checkTokenExpiry, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated, userInfo, authMode]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        userInfo,
        isTokenExpired,
        authMode,
        login,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
